import React, { useEffect, useState } from "react";
import { configurationService } from "../services/configurationService";
import { DEFAULT_ON_PIPE_ASSUMPTIONS } from "../domain/defaultAssumptions";
import {
  calculateSingleSideCoatingBuildUpMm,
  parseMeasureMm,
} from "../domain/coatingBuildUp";
import { buildPipeGeometryTiles } from "./PipeGeometryTiles";
import type {
  InputSummarySection,
  PipeSpecification,
  ProcessSpecification,
} from "../models/types";

/** Spec Calculator / On-Pipe meter is always drawn on a 0–100 mm scale. */
const DISPLAY_SCALE_MAX_MM = 100;

const formatMm = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, "");

interface StepDownThresholdGaugeProps {
  pipe: PipeSpecification;
  process: ProcessSpecification;
  inputSummary?: InputSummarySection[];
}

const StepDownThresholdGauge = ({
  pipe,
  process,
  inputSummary,
}: StepDownThresholdGaugeProps) => {
  const [thresholdMm, setThresholdMm] = useState(
    DEFAULT_ON_PIPE_ASSUMPTIONS.inductionCoil.stepDownCoatingThicknessMm,
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const assumptions = await configurationService.getAssumptions();
        if (!cancelled) {
          setThresholdMm(assumptions.inductionCoil.stepDownCoatingThicknessMm);
        }
      } catch {
        /* keep default threshold */
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const coatingTile = buildPipeGeometryTiles(pipe, process, inputSummary).find(
    (tile) => tile.label === "Coating build-up",
  );
  const fromSummary = parseMeasureMm(
    coatingTile && coatingTile.value !== "—"
      ? `${coatingTile.value}${coatingTile.unit ? ` ${coatingTile.unit}` : ""}`
      : null,
  );
  const fromInputs = calculateSingleSideCoatingBuildUpMm(process, pipe);
  const buildUpMm = fromSummary ?? fromInputs;

  if (buildUpMm == null) return null;

  const valuePct = Math.min(100, Math.max(0, (buildUpMm / DISPLAY_SCALE_MAX_MM) * 100));
  const thresholdPct = Math.min(100, Math.max(0, (thresholdMm / DISPLAY_SCALE_MAX_MM) * 100));
  const considerStepDown = buildUpMm > thresholdMm;
  const infoText =
    "singleSideCoatingBuildUp = coatingThickness.nominal + coatingPlusTolerance × 2. " +
    "Step-down is recommended when build-up exceeds the Coil step-down threshold assumption.";

  return (
    <section className="op-threshold-gauge" aria-label="Coating build-up vs step-down threshold">
      <div className="op-threshold-gauge__head">
        <div className="op-threshold-gauge__title-row">
          <h3 className="op-threshold-gauge__title">
            Coating build-up vs. step-down threshold
          </h3>
          <span className="op-threshold-gauge__info" title={infoText} aria-label={infoText}>
            i
          </span>
        </div>
        <div className="op-threshold-gauge__value">
          {formatMm(buildUpMm)} <span>mm</span>
        </div>
      </div>

      <div className="op-threshold-gauge__track-wrap">
        <div className="op-threshold-gauge__track">
          <div
            className="op-threshold-gauge__fill"
            style={{ width: `${valuePct}%` }}
            aria-hidden
          />
          <div
            className="op-threshold-gauge__marker"
            style={{ left: `${valuePct}%` }}
            aria-hidden
          />
        </div>
        <div className="op-threshold-gauge__scale">
          <span>0</span>
          <span
            className="op-threshold-gauge__threshold-label"
            style={{ left: `${thresholdPct}%` }}
          >
            {formatMm(thresholdMm)} mm threshold
          </span>
          <span>{DISPLAY_SCALE_MAX_MM} mm</span>
        </div>
      </div>

      <p className="op-threshold-gauge__hint">
        Step-Down Config: {considerStepDown ? "Consider Step Down" : "No"}
      </p>
    </section>
  );
};

export default StepDownThresholdGauge;
